import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/Button";
import { siteConfig } from "@/data/siteConfig";

const line1 = "Tractor Parts";
const line2 = "Built for Dependable";
const line3 = "Performance";

function MaskedLine({ text, delay, reduce, className = "" }) {
  return (
    <motion.span
      className={`block ${className}`}
      initial={{ opacity: 0, y: reduce ? 0 : 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  );
}

export function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ink text-white" data-testid="hero">
      <div className="tech-grid-dark absolute inset-0" />
      <div className="absolute left-0 top-0 h-full w-px bg-white/10 sm:left-6" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 py-16 lg:py-28 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5 font-body text-[11px] font-semibold uppercase tracking-[0.25em] text-amber">
            <span className="h-1.5 w-1.5 bg-amber" /> Tractor & Backhoe Loader Parts · International Supply
          </motion.span>
          <h1 className="font-display font-bold uppercase tracking-tight text-[clamp(38px,6.4vw,76px)] leading-[0.95]">
            <MaskedLine text={line1} delay={0.15} reduce={reduce} />
            <MaskedLine text={line2} delay={0.28} reduce={reduce} />
            <MaskedLine text={line3} delay={0.41} reduce={reduce} className="text-amber" />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-7 max-w-xl text-[clamp(16px,2vw,18px)] leading-relaxed text-white/70">
            {siteConfig.company.shortName} supplies aftermarket parts for agricultural tractors, implements, and Case &amp; JCB backhoe loaders — with 3-point linkage parts as our core strength — through our Terramech Components Inc. and LAV-TEK divisions, backed by dependable, durable components and professional international support.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-9 flex flex-wrap gap-4">
            <Button to="/request-a-quote" variant="accent" arrow data-testid="hero-quote-btn">Request a Quote</Button>
            <Button to="/request-a-quote" variant="outline" data-testid="hero-part-btn">Send Part Number</Button>
          </motion.div>
          <div className="mt-12 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 max-w-lg">
            {["Multi-Brand Parts", "Durable Components", "Export Heritage"].map((t) => (
              <div key={t} className="font-body text-xs font-semibold uppercase tracking-wider text-white/50">{t}</div>
            ))}
          </div>
        </div>

        <motion.div
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative">
          <div className="clip-notch relative overflow-hidden border border-white/10">
            <motion.img
              style={{ y: imgY }}
              src="/images/ocean-exports-tractor-implement-parts-warehouse.jpg"
              alt="Ocean Exports aftermarket tractor and implement parts — sprayer, trailer, seed drill, disc harrow, 3-point linkage, combine harvester and spare parts tray in a warehouse yard"
              className="h-[340px] sm:h-[460px] lg:h-[560px] w-full object-cover"
              loading="eager" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-3 border border-amber/50 bg-ink px-5 py-3">
            <span className="font-display text-3xl font-bold text-amber">OE</span>
            <span className="text-xs uppercase tracking-widest text-white/70 leading-tight">Precision<br />Sourcing</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
